import { useState } from 'react';
import axios from 'axios';
import { api } from '@/api/client';

type FeedbackKind = 'correct' | 'partial' | 'wrong';
type FooterState = 'idle' | 'sending' | 'sent' | 'error';

interface FeedbackFooterProps {
  auditId: string;
}

function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.response?.status === 422) {
      return 'Feedback was rejected — invalid verdict.';
    }
    if (err.response?.status === 404) {
      return 'Feedback already recorded for this answer.';
    }
  }
  return 'Could not record feedback — try again.';
}

export function FeedbackFooter({ auditId }: FeedbackFooterProps) {
  const [state, setState] = useState<FooterState>('idle');
  const [error, setError] = useState<string | null>(null);

  const send = async (kind: FeedbackKind) => {
    let notes = '';
    if (kind === 'wrong') {
      notes = window.prompt('Why is this answer wrong? (>= 20 chars)') ?? '';
      if (notes.trim().length < 20) return;
    }
    setState('sending');
    setError(null);
    try {
      await api.post(`/ask-ai/${auditId}/feedback`, { feedback: kind, notes });
      setState('sent');
    } catch (err) {
      setError(errorMessage(err));
      setState('error');
    }
  };

  if (state === 'sent') {
    return <div className="text-xs text-muted-foreground">Thanks — logged.</div>;
  }

  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-2">
        <button
          type="button"
          disabled={state === 'sending'}
          className="text-xs text-emerald-700 hover:underline disabled:opacity-50"
          onClick={() => void send('correct')}
        >
          Mark correct
        </button>
        <button
          type="button"
          disabled={state === 'sending'}
          className="text-xs text-amber-700 hover:underline disabled:opacity-50"
          onClick={() => void send('partial')}
        >
          Mark partial
        </button>
        <button
          type="button"
          disabled={state === 'sending'}
          className="text-xs text-red-700 hover:underline disabled:opacity-50"
          onClick={() => void send('wrong')}
        >
          Mark wrong
        </button>
      </div>
      {state === 'error' && error && (
        <div className="text-xs text-destructive">{error}</div>
      )}
    </div>
  );
}

export default FeedbackFooter;
